import { Command } from 'commander'
import fs from 'node:fs'
import { output } from '@/helpers/std-out'
import getPort from 'get-port'
import { createServer } from '@scalar-org/guide-preview/server'
import fastifyStatic from '@fastify/static'
import path from 'node:path'
import { getLocalIP } from '@/helpers/local-address'
import as from 'ansis'
import type { RouteHandlerMethod } from 'fastify'
import { openBrowser } from '@/helpers/url-opener'

export const PreviewCommand = () => {
  const cmd = new Command('preview')

  cmd.description('Preview scalar guides')
  cmd.argument('[config]', 'your config file of the project')
  cmd.action(async (configPath) => {
    if (!configPath) {
      return output
        .error()
        .title('Invalid configuration file')
        .message('Please provide a valid configuration file path')
        .exit('error')
    }

    if (!fs.existsSync(configPath)) {
      return output
        .error()
        .title('Invalid configuration file')
        .message('Could not find the Scalar Configuration file:')
        .message(`'${configPath ?? ''}'`)
        .exit('error')
    }

    const host = '0.0.0.0'
    const port = await getPort({
      port: 7970, // Preferred port
    })

    const server = await createServer(
      configPath,
      // Skip stack trace
      (message) => console.log(message),
      {
        proxy: 'https://proxy.scalar.com',
      },
    )

    // Serve static files
    await server.register(fastifyStatic, {
      root: path.join(__dirname, './client'),
    })

    /**
     * Handle serving the static file
     */
    const handler: RouteHandlerMethod = (_, reply) => {
      // Read the static file
      const filePath = path.join(__dirname, './client/index.html')
      const fileContents = fs.readFileSync(filePath, 'utf-8')

      // Build the configuration
      const configuration = [
        '<script id="config" type="application/json">',
        JSON.stringify({ port }),
        '</script>',
      ].join('\n')

      reply
        .type('text/html')
        .send(
          fileContents.replace(
            '<!-- Configuration Replacement Here -->',
            configuration,
          ),
        )
    }

    /**
     * We need to explicitly handle `/` since fastify static is going
     * to serve the raw html file without our custom logic
     */
    server.get('/', handler)
    /**
     * Since fastify static is registered on the root, it already
     * handles all the routes to `/*`. We serve the index file when the
     * fastify static fails to resolve the static files
     */
    server.setNotFoundHandler(handler)

    // Spin up local server to serve the preview of the scalar guide project
    await server.listen({ port, host })
    output
      .info()
      .message(as.green('Server listening at:'))
      .table([
        ['Local: ', as.blueBright(`http://localhost:${port}`)],
        ['On your network: ', as.blueBright(`http://${getLocalIP()}:${port}`)],
      ])
      .line()
      .print()

    // Open the browser window
    openBrowser(`http://localhost:${port}`)
  })

  return cmd
}
